import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Confirm } from "./Shared";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Plus, Pencil, Trash2, Check, X, GripVertical, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export function ListManager({ title, description, endpoint, testPrefix }) {
  const listKey = endpoint.replace(/^\//, "");
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState("");
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [dragIdx, setDragIdx] = useState(null);

  const singular = title.replace(/s$/, "");
  const load = useCallback(() => { api.get(endpoint).then(({ data }) => setItems(data)).catch(() => {}); }, [endpoint]);
  useEffect(load, [load]);

  const add = async () => {
    const name = adding.trim();
    if (!name || busy) return;
    setBusy(true);
    try { await api.post(endpoint, { name }); setAdding(""); load(); toast.success(`${singular} added`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addBulk = async () => {
    const text = bulkText.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/master-lists/${listKey}/bulk`, { text });
      setBulkText(""); setBulkOpen(false); load();
      toast.success(`Added ${data.added}${data.skipped ? `, ${data.skipped} skipped (duplicates)` : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveEdit = async (id) => {
    const name = editVal.trim();
    if (!name || busy) return;
    setBusy(true);
    try { await api.put(`${endpoint}/${id}`, { name }); setEditId(null); load(); toast.success("Updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`${endpoint}/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const onDrop = async (toIdx) => {
    if (dragIdx === null || dragIdx === toIdx) { setDragIdx(null); return; }
    const arr = [...items];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(toIdx, 0, moved);
    setItems(arr); setDragIdx(null);
    try { await api.post(`/master-lists/${listKey}/reorder`, { ids: arr.map((i) => i.id) }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
  };

  return (
    <div data-testid={`${testPrefix}-manager`} className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="font-medium text-slate-800 text-sm">{title}</div>
      {description && <div className="text-xs text-slate-500 mb-3 mt-0.5">{description}</div>}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Input data-testid={`${testPrefix}-new-input`} value={adding} placeholder={`Add ${singular.toLowerCase()}…`}
          className="flex-1 min-w-[8rem] h-9 text-sm"
          onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button data-testid={`${testPrefix}-add-button`} onClick={add} disabled={busy} className="bg-[#1E3A8A] hover:bg-[#1D4ED8] shrink-0"><Plus className="h-4 w-4" /></Button>
        <Button data-testid={`${testPrefix}-bulk-toggle`} variant="outline" onClick={() => setBulkOpen((o) => !o)} className="shrink-0 gap-1 h-9 text-xs"><ClipboardList className="h-3.5 w-3.5" /> Bulk</Button>
      </div>

      {bulkOpen && (
        <div className="mb-3 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-2">
          <Textarea data-testid={`${testPrefix}-bulk-input`} value={bulkText} rows={4}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={`Paste ${title.toLowerCase()} — one per line or comma-separated`} className="text-sm" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setBulkOpen(false); setBulkText(""); }}>Cancel</Button>
            <Button size="sm" data-testid={`${testPrefix}-bulk-save`} onClick={addBulk} disabled={busy || !bulkText.trim()} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Add all</Button>
          </div>
        </div>
      )}

      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
        {items.map((it, idx) => (
          <div key={it.id} data-testid={`${testPrefix}-item-${it.id}`}
            draggable={editId !== it.id}
            onDragStart={() => setDragIdx(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
            className={`flex items-center gap-2 rounded-md border border-slate-100 bg-slate-50 px-2 py-1.5 ${dragIdx === idx ? "opacity-50" : ""}`}>
            {editId === it.id ? (
              <>
                <Input data-testid={`${testPrefix}-edit-input`} value={editVal} onChange={(e) => setEditVal(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveEdit(it.id)}
                  className="flex-1 h-8 text-sm" autoFocus />
                <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" data-testid={`${testPrefix}-save-${it.id}`} onClick={() => saveEdit(it.id)}><Check className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400" data-testid={`${testPrefix}-cancel-${it.id}`} onClick={() => setEditId(null)}><X className="h-4 w-4" /></Button>
              </>
            ) : (
              <>
                <GripVertical className="h-4 w-4 text-slate-300 cursor-grab shrink-0" />
                <span className="flex-1 min-w-0 truncate text-sm text-slate-700" title={it.name}>{it.name}</span>
                {it.usage > 0 && (
                  <span data-testid={`${testPrefix}-usage-${it.id}`} className="shrink-0 text-[10px] whitespace-nowrap text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">in use · {it.usage}</span>
                )}
                <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`${testPrefix}-edit-${it.id}`} onClick={() => { setEditId(it.id); setEditVal(it.name); }}><Pencil className="h-3.5 w-3.5" /></Button>
                {it.usage > 0 ? (
                  <Button size="icon" variant="ghost" disabled className="h-8 w-8 text-red-300" title={`In use by ${it.usage} record(s) — cannot delete`} data-testid={`${testPrefix}-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                ) : (
                  <Confirm onConfirm={() => remove(it.id)} title={`Delete "${it.name}"?`} description="This removes it from the dropdown for new records. Existing records keep their value."
                    trigger={<Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" data-testid={`${testPrefix}-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>} />
                )}
              </>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-slate-400 py-2">Nothing here yet.</div>}
      </div>
    </div>
  );
}
